import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  FaCalendarAlt, 
  FaFileExport, 
  FaSearch,
  FaEraser,
  FaFileInvoice,
  FaUser,
  FaBuilding,
  FaFilter,
  FaSync,
  FaTags,
  FaArrowLeft,
  FaArrowRight,
  FaRedo,
  FaExclamationTriangle,
  FaEdit,
  FaPrint,
  FaCheckCircle,
  FaBoxes
} from 'react-icons/fa';
import { BsFiletypeCsv, BsFileEarmarkExcel } from 'react-icons/bs';

export default function DeliveryOrderReport({ readOnly = false } = {}) {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    employeeId: null
  });
  const [doSearch, setDoSearch] = useState('');

  // Tab state
  const [activeReportTab, setActiveReportTab] = useState('regular');

  // Tiles DO state
  const [tilesLoading, setTilesLoading] = useState(false);
  const [tilesReportData, setTilesReportData] = useState(null);
  const [tilesPage, setTilesPage] = useState(1);
  const [tilesDoSearch, setTilesDoSearch] = useState('');
  const [tilesFilters, setTilesFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    employeeId: null
  });
  const [tilesDateEditModalOpen, setTilesDateEditModalOpen] = useState(false);
  const [tilesSelectedOrder, setTilesSelectedOrder] = useState(null);
  const [tilesNewDate, setTilesNewDate] = useState(null);
  const [tilesDateEditLoading, setTilesDateEditLoading] = useState(false);
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const sync = () => setDarkMode(localStorage.getItem('darkMode') === 'true');
    window.addEventListener('storage', sync);
    const interval = setInterval(sync, 1000);
    return () => { window.removeEventListener('storage', sync); clearInterval(interval); };
  }, []);
  
  // Reset order state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [selectedOrderToReset, setSelectedOrderToReset] = useState(null);
  const [resetReason, setResetReason] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Edit date state
  const [isDateEditModalOpen, setIsDateEditModalOpen] = useState(false);
  const [selectedOrderForDateEdit, setSelectedOrderForDateEdit] = useState(null);
  const [newCreatedDate, setNewCreatedDate] = useState(null);
  const [dateEditLoading, setDateEditLoading] = useState(false);

  // Bulk complete state
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showBulkCompleteModal, setShowBulkCompleteModal] = useState(false);
  const [bulkUseSingleInvoice, setBulkUseSingleInvoice] = useState(false);
  const [bulkSingleInvoiceNumber, setBulkSingleInvoiceNumber] = useState('');
  const [bulkInvoiceNumbers, setBulkInvoiceNumbers] = useState({});
  const [bulkCompleteLoading, setBulkCompleteLoading] = useState(false);
  const [bulkSelectLoading, setBulkSelectLoading] = useState(false);
  const [bulkOrderCache, setBulkOrderCache] = useState({});

  // Options for react-select
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [statusOptions] = useState([
    { value: '', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Completed', label: 'Completed' }
  ]);

  // Dark mode helper classes
  const cardCls = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textM = darkMode ? 'text-gray-500' : 'text-gray-400';
  const borderCls = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputCls = darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900';
  const hoverRow = darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50';
  const theadCls = darkMode ? 'bg-gray-750' : 'bg-gray-50';

  const getCompanyId = () => {
    return user?.user?.company._id || user?.user?.company;
  };

  const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setFilters({
      startDate: toLocalDateStr(startOfMonth),
      endDate: toLocalDateStr(now),
      status: '',
      employeeId: null
    });
    setTilesFilters({
      startDate: toLocalDateStr(startOfMonth),
      endDate: toLocalDateStr(now),
      status: '',
      employeeId: null
    });
  }, []);

  useEffect(() => {
    const companyId = getCompanyId();
    if (companyId) fetchEmployeeOptions();
  }, [user]);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      if (startDate > endDate) return;
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        fetchDeliveryOrdersData();
      }
    }
  }, [page, limit, filters.startDate, filters.endDate, filters.status, filters.employeeId]);

  useEffect(() => {
    if (activeReportTab === 'tiles' && tilesFilters.startDate && tilesFilters.endDate) {
      const startDate = new Date(tilesFilters.startDate);
      const endDate = new Date(tilesFilters.endDate);
      if (startDate > endDate) return;
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        fetchTilesDeliveryOrders();
      }
    }
  }, [activeReportTab, tilesPage, tilesFilters.startDate, tilesFilters.endDate, tilesFilters.status, tilesFilters.employeeId]);

  const fetchEmployeeOptions = async () => {
    try {
      const companyId = getCompanyId();
      if (!companyId) return;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/employees/company2/${companyId}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const empList = data.employees || data;
        const options = empList.map(emp => ({
          value: emp._id,
          label: `${emp.name} (${emp.id || 'No ID'})`
        }));
        setEmployeeOptions([{ value: '', label: 'All Employees' }, ...options]);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDeliveryOrdersData = async () => {
    if (!filters.startDate || !filters.endDate) {
      Swal.fire({ icon: 'error', title: 'Missing Required Fields', text: 'Please select both start and end dates to continue' });
      return;
    }
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      Swal.fire({ icon: 'error', title: 'Invalid Dates', text: 'Please ensure both dates are valid' });
      return;
    }
    if (startDate > endDate) {
      Swal.fire({ icon: 'error', title: 'Invalid Date Range', text: 'Start date cannot be after end date' });
      return;
    }
    const dayDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (dayDiff > 90) {
      const proceed = await Swal.fire({
        icon: 'warning', title: 'Large Date Range',
        text: `You've selected a ${dayDiff} day range. This may take longer to load. Continue?`,
        showCancelButton: true, confirmButtonText: 'Continue', cancelButtonText: 'Adjust Range'
      });
      if (!proceed.isConfirmed) return;
    }
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        page, limit,
        startDate: filters.startDate,
        endDate: filters.endDate,
        ...(filters.status && { status: filters.status }),
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId }),
        ...(doSearch.trim() && { doNumber: doSearch.trim() })
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length === 0) {
          Swal.fire({ icon: 'info', title: 'No Data Found', text: 'No delivery orders found with the current filter criteria.' });
        }
        setReportData(data);
      } else {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        if (response.status === 400) {
          Swal.fire({ icon: 'error', title: 'Invalid Request', text: 'Please check your filter values and try again.' });
        } else if (response.status === 401 || response.status === 403) {
          Swal.fire({ icon: 'error', title: 'Authentication Error', text: 'You do not have permission to view this report.' });
        } else {
          throw new Error(`Failed to fetch delivery orders data: ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire('Error', `Failed to fetch delivery orders report data: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    let newFilters = { ...filters, [field]: value };
    if (field !== 'startDate' && field !== 'endDate') setPage(1);
    if (field === 'startDate' || field === 'endDate') {
      const sDate = field === 'startDate' ? value : filters.startDate;
      const eDate = field === 'endDate' ? value : filters.endDate;
      if (sDate && eDate) {
        const start = new Date(sDate);
        const end = new Date(eDate);
        const now = new Date(); now.setHours(23, 59, 59, 999);
        if (end > now) {
          Swal.fire({ icon: 'info', title: 'Date Adjusted', text: 'End date cannot be in the future. Set to today.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
          newFilters.endDate = toLocalDateStr(now);
        }
        if (start > end) {
          Swal.fire({ icon: 'warning', title: 'Invalid Date Range', text: 'Start date cannot be after end date. Start date adjusted.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
          newFilters.startDate = newFilters.endDate;
        }
      }
    }
    setFilters(newFilters);
  };

  const handleDoSearch = () => { setPage(1); fetchDeliveryOrdersData(); };
  const handlePageChange = (newPage) => { setPage(newPage); };

  const resetFilters = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setFilters({ startDate: toLocalDateStr(startOfMonth), endDate: toLocalDateStr(now), status: '', employeeId: null });
    setDoSearch('');
    setPage(1);
    setTimeout(() => { fetchDeliveryOrdersData(); }, 0);
    Swal.fire({ position: 'top-end', icon: 'success', title: 'Filters have been reset', text: `Date range set to ${formatDate(startOfMonth)} - ${formatDate(now)}`, showConfirmButton: false, timer: 2000 });
  };

  const exportToCSV = async () => {
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.status && { status: filters.status }),
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId }),
        format: 'csv'
      });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders/export?${params}`, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Delivery_Orders_Report_${filters.startDate}_to_${filters.endDate}.csv`);
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'CSV file downloaded successfully', 'success');
      } else { throw new Error('Failed to download CSV file'); }
    } catch (error) {
      Swal.fire('Error', 'Failed to download CSV file', 'error');
    } finally { setLoading(false); }
  };

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.status && { status: filters.status }),
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId }),
        format: 'excel'
      });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders/export?${params}`, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Delivery_Orders_Report_${filters.startDate}_to_${filters.endDate}.xlsx`);
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      } else { throw new Error('Failed to download Excel file'); }
    } catch (error) {
      Swal.fire('Error', 'Failed to download Excel file', 'error');
    } finally { setLoading(false); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    let cls = 'px-2 py-1 rounded text-xs font-medium';
    switch (status?.toLowerCase()) {
      case 'pending': cls += darkMode ? ' bg-yellow-900/40 text-yellow-300' : ' bg-yellow-100 text-yellow-800'; break;
      case 'cancelled': cls += darkMode ? ' bg-red-900/40 text-red-300' : ' bg-red-100 text-red-800'; break;
      case 'completed': cls += darkMode ? ' bg-green-900/40 text-green-300' : ' bg-green-100 text-green-800'; break;
      default: cls += darkMode ? ' bg-gray-700 text-gray-300' : ' bg-gray-100 text-gray-800'; break;
    }
    return <span className={cls}>{status || 'Unknown'}</span>;
  };

  const openResetModal = (order) => { setSelectedOrderToReset(order); setResetReason(''); setIsResetModalOpen(true); };
  const closeResetModal = () => { setIsResetModalOpen(false); setSelectedOrderToReset(null); setResetReason(''); };

  const getFinancialYearBounds = (dateStr) => {
    const d = new Date(dateStr);
    const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return { fyStart: new Date(fyStartYear, 3, 1), fyEnd: new Date(fyStartYear + 1, 2, 31) };
  };

  const openDateEditModal = (order) => { setSelectedOrderForDateEdit(order); setNewCreatedDate(new Date(order.createdAt)); setIsDateEditModalOpen(true); };
  const closeDateEditModal = () => { setIsDateEditModalOpen(false); setSelectedOrderForDateEdit(null); setNewCreatedDate(null); };

  const updateDeliveryOrderDate = async () => {
    if (!selectedOrderForDateEdit || !newCreatedDate) { Swal.fire('Error', 'Please select a valid date', 'error'); return; }
    setDateEditLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/delivery-orders/${selectedOrderForDateEdit._id}/edit-created-date`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ createdAt: newCreatedDate.toISOString(), updatedBy: user?.user?._id }), credentials: 'include' }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        closeDateEditModal();
        Swal.fire('Success', 'Delivery order date updated successfully', 'success');
        fetchDeliveryOrdersData();
      } else { Swal.fire('Error', result.message || 'Failed to update date', 'error'); }
    } catch (error) {
      Swal.fire('Error', 'Failed to update delivery order date', 'error');
    } finally { setDateEditLoading(false); }
  };

  const resetDeliveryOrder = async () => {
    if (!selectedOrderToReset || !resetReason.trim()) { Swal.fire('Error', 'Please provide a reason for resetting the delivery order', 'error'); return; }
    setResetLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-order-reset/${selectedOrderToReset._id}/reset`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resetReason, resetBy: user?.user?._id, resetByModel: 'CompanyHead' }), credentials: 'include' }
      );
      if (response.ok) {
        closeResetModal();
        Swal.fire({ position: 'center', icon: 'success', title: 'Delivery order has been reset successfully', showConfirmButton: true });
        fetchDeliveryOrdersData();
      } else {
        const errorData = await response.json();
        if (response.status === 401) Swal.fire('Authentication Error', 'You are not authorized to reset delivery orders.', 'error');
        else if (response.status === 403) Swal.fire('Permission Denied', 'You do not have permission to reset this delivery order.', 'error');
        else throw new Error(errorData.message || 'Failed to reset delivery order');
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Reset Failed', text: error.message || 'Failed to reset delivery order.' });
    } finally { setResetLoading(false); }
  };

  const selectStyles = {
    control: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : '#fff', borderColor: darkMode ? '#4B5563' : '#D1D5DB', color: darkMode ? '#fff' : '#111', '&:hover': { borderColor: darkMode ? '#6B7280' : '#9CA3AF' } }),
    menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : '#fff', zIndex: 50 }),
    option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? (darkMode ? '#4B5563' : '#EBF5FF') : 'transparent', color: darkMode ? '#E5E7EB' : '#111' }),
    singleValue: (base) => ({ ...base, color: darkMode ? '#E5E7EB' : '#111' }),
    placeholder: (base) => ({ ...base, color: darkMode ? '#9CA3AF' : '#6B7280' }),
    input: (base) => ({ ...base, color: darkMode ? '#E5E7EB' : '#111' })
  };

  // Bulk complete helpers
  const pendingOrders = reportData?.data?.filter(o => o.status === 'Pending') || [];
  const allPendingSelected = pendingOrders.length > 0 && pendingOrders.every(o => selectedOrders.includes(o._id));

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const toggleSelectAllPending = () => {
    if (allPendingSelected) {
      setSelectedOrders(prev => prev.filter(id => !pendingOrders.find(o => o._id === id)));
    } else {
      const pendingIds = pendingOrders.map(o => o._id);
      setSelectedOrders(prev => [...new Set([...prev, ...pendingIds])]);
    }
  };

  const openBulkCompleteModal = () => {
    const invoiceMap = {};
    selectedOrders.forEach(id => { invoiceMap[id] = ''; });
    setBulkUseSingleInvoice(selectedOrders.length > 1);
    setBulkSingleInvoiceNumber('');
    setBulkInvoiceNumbers(invoiceMap);
    setShowBulkCompleteModal(true);
  };

  const closeBulkCompleteModal = () => {
    setShowBulkCompleteModal(false);
    setBulkUseSingleInvoice(false);
    setBulkSingleInvoiceNumber('');
    setBulkInvoiceNumbers({});
  };

  const handleBulkComplete = async () => {
    const singleInvoice = bulkSingleInvoiceNumber.trim();
    const allFilled = bulkUseSingleInvoice
      ? Boolean(singleInvoice)
      : selectedOrders.every(id => bulkInvoiceNumbers[id]?.trim());

    if (!allFilled) {
      Swal.fire('Error', bulkUseSingleInvoice ? 'Please enter the invoice number' : 'Please enter invoice numbers for all selected orders', 'error');
      return;
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Complete Selected Orders?',
      text: `This will mark ${selectedOrders.length} order(s) as Completed. This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Complete All',
      confirmButtonColor: '#16a34a'
    });
    if (!confirm.isConfirmed) return;

    setBulkCompleteLoading(true);
    try {
      const orders = selectedOrders.map(id => ({
        orderId: id,
        invoiceNumber: bulkUseSingleInvoice ? singleInvoice : bulkInvoiceNumbers[id].trim()
      }));
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/bulk-update-status`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ orders }), credentials: 'include' }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        closeBulkCompleteModal();
        setSelectedOrders([]);
        setBulkOrderCache({});
        Swal.fire('Success', result.message, 'success');
        fetchDeliveryOrdersData();
      } else {
        Swal.fire('Error', result.message || 'Failed to complete orders', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to complete orders: ' + error.message, 'error');
    } finally {
      setBulkCompleteLoading(false);
    }
  };

  const selectPendingUpTo = async (n) => {
    if (!filters.startDate || !filters.endDate) {
      Swal.fire({ icon: 'error', title: 'Missing Required Fields', text: 'Please select both start and end dates' });
      return;
    }
    setBulkSelectLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        page: 1, limit: n,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: 'Pending',
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId }),
        ...(doSearch.trim() && { doNumber: doSearch.trim() })
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders?${params}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to load pending orders');
      const data = await response.json();
      const pending = (data.data || []).filter(o => o.status === 'Pending');
      const cache = {};
      pending.forEach(o => { cache[o._id] = o; });
      setBulkOrderCache(prev => ({ ...prev, ...cache }));
      setSelectedOrders(pending.map(o => o._id));
      if (pending.length === 0) {
        Swal.fire({ icon: 'info', title: 'No Pending Orders', text: 'No pending delivery orders found with the current filters.' });
      }
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setBulkSelectLoading(false);
    }
  };

  // ===== Tiles Delivery Orders Functions =====
  const fetchTilesDeliveryOrders = async () => {
    if (!tilesFilters.startDate || !tilesFilters.endDate) return;
    setTilesLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        page: tilesPage, limit: 10,
        startDate: tilesFilters.startDate,
        endDate: tilesFilters.endDate,
        ...(tilesFilters.status && { status: tilesFilters.status }),
        ...(tilesFilters.employeeId && { employeeId: tilesFilters.employeeId.value }),
        ...(companyId && { companyId }),
        ...(tilesDoSearch.trim() && { search: tilesDoSearch.trim(), searchType: 'doNumber' })
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/delivery-orders?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.orders && data.orders.length === 0) {
          Swal.fire({ icon: 'info', title: 'No Data Found', text: 'No tiles delivery orders found with the current filter criteria.' });
        }
        setTilesReportData(data);
      } else {
        throw new Error(`Failed to fetch tiles delivery orders: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching tiles delivery orders:', error);
      Swal.fire('Error', `Failed to fetch tiles delivery orders: ${error.message}`, 'error');
    } finally {
      setTilesLoading(false);
    }
  };

  const handleTilesFilterChange = (field, value) => {
    let newFilters = { ...tilesFilters, [field]: value };
    if (field !== 'startDate' && field !== 'endDate') setTilesPage(1);
    if (field === 'startDate' || field === 'endDate') {
      const sDate = field === 'startDate' ? value : tilesFilters.startDate;
      const eDate = field === 'endDate' ? value : tilesFilters.endDate;
      if (sDate && eDate) {
        const start = new Date(sDate);
        const end = new Date(eDate);
        const now = new Date(); now.setHours(23, 59, 59, 999);
        if (end > now) {
          newFilters.endDate = toLocalDateStr(now);
        }
        if (start > end) {
          newFilters.startDate = newFilters.endDate;
        }
      }
    }
    setTilesFilters(newFilters);
  };

  const handleTilesDoSearch = () => { setTilesPage(1); fetchTilesDeliveryOrders(); };

  const resetTilesFilters = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setTilesFilters({ startDate: toLocalDateStr(startOfMonth), endDate: toLocalDateStr(now), status: '', employeeId: null });
    setTilesDoSearch('');
    setTilesPage(1);
    setTimeout(() => { fetchTilesDeliveryOrders(); }, 0);
  };

  const openTilesDateEditModal = (order) => {
    const recipientName = order.recipient?.name || order.recipient?.companyName || order.recipientInfo?.name || order.recipientInfo?.companyName || '—';
    setTilesSelectedOrder({ ...order, recipientName });
    setTilesNewDate(new Date(order.createdAt));
    setTilesDateEditModalOpen(true);
  };
  const closeTilesDateEditModal = () => { setTilesDateEditModalOpen(false); setTilesSelectedOrder(null); setTilesNewDate(null); };

  const updateTilesDeliveryOrderDate = async () => {
    if (!tilesSelectedOrder || !tilesNewDate) { Swal.fire('Error', 'Please select a valid date', 'error'); return; }
    setTilesDateEditLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles/delivery-orders/${tilesSelectedOrder._id}/edit-created-date`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ createdAt: tilesNewDate.toISOString(), updatedBy: user?.user?._id }), credentials: 'include' }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        closeTilesDateEditModal();
        Swal.fire('Success', 'Tiles delivery order date updated successfully', 'success');
        fetchTilesDeliveryOrders();
      } else { Swal.fire('Error', result.message || 'Failed to update date', 'error'); }
    } catch (error) {
      Swal.fire('Error', 'Failed to update tiles delivery order date', 'error');
    } finally { setTilesDateEditLoading(false); }
  };

  const handleTilesPrint = (id, printType = 'basic') => {
    const url = printType === 'discount' ? `/tiles-delivery-orders-discount/${id}` : `/tiles-delivery-orders/${id}`;
    window.open(url, '_blank');
  };

  const getTilesRecipientName = (order) => {
    if (order.recipient?.name) return order.recipient.name;
    if (order.recipient?.companyName) return order.recipient.companyName;
    if (order.recipientInfo?.name) return order.recipientInfo.name;
    if (order.recipientInfo?.companyName) return order.recipientInfo.companyName;
    return '—';
  };

  const getTilesCreatedByName = (order) => {
    if (order.createdBy?.name) return order.createdBy.name;
    if (order.createdByInfo && order.createdByInfo.length > 0) return order.createdByInfo[0].name;
    return '—';
  };

  const getTilesSalesmanNames = (order) => {
    if (order.salesman && Array.isArray(order.salesman) && order.salesman.length > 0) {
      return order.salesman.map(s => s.name || s).filter(Boolean).join(', ');
    }
    if (order.salesmanInfo && order.salesmanInfo.length > 0) {
      return order.salesmanInfo.map(s => s.name).join(', ');
    }
    return '—';
  };

  const getTilesLocationName = (order) => {
    if (order.location?.name) return order.location.name;
    if (order.locationInfo && order.locationInfo.length > 0) return order.locationInfo[0].name;
    return '—';
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-2xl font-bold ${textP} mb-2 flex items-center`}>
            <FaFileInvoice className="mr-2" /> Delivery Orders Report
          </h1>
          <p className={textS}>View and analyze delivery orders, track performance, and export data.</p>
        </div>

        {/* Tab Switcher */}
        <div className={`flex space-x-1 mb-6 ${cardCls} rounded-lg shadow-md border p-1`}>
          <button
            onClick={() => setActiveReportTab('regular')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
              activeReportTab === 'regular'
                ? 'bg-blue-600 text-white shadow-sm'
                : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <FaFileInvoice /> Regular Delivery Orders
          </button>
          <button
            onClick={() => setActiveReportTab('tiles')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
              activeReportTab === 'tiles'
                ? 'bg-blue-600 text-white shadow-sm'
                : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <FaBoxes /> Tiles Delivery Orders
          </button>
        </div>

        {/* Regular DO Tab */}
        {activeReportTab === 'regular' && (<>
        {/* Filters */}
        <div className={`${cardCls} rounded-lg shadow-md border p-4 mb-6`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className={`text-lg font-medium flex items-center ${textP}`}>
              <FaFilter className={`mr-2 ${textS}`} /> Report Filters
            </h2>
            <button className={`flex items-center px-4 py-2 rounded text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`} onClick={resetFilters}>
              <FaEraser className="mr-2" /> Reset
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className={`block text-sm font-medium ${textS} mb-1`}>
                <FaCalendarAlt className="inline mr-1" /> Start Date <span className="text-red-500">*</span>
              </label>
              <input type="date" className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls} ${!filters.startDate ? 'border-red-400' : ''}`} value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} max={toLocalDateStr(new Date())} required />
              {!filters.startDate && <p className="mt-1 text-xs text-red-500">Start date is required</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium ${textS} mb-1`}>
                <FaCalendarAlt className="inline mr-1" /> End Date <span className="text-red-500">*</span>
              </label>
              <input type="date" className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls} ${!filters.endDate ? 'border-red-400' : ''}`} value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} min={filters.startDate} max={toLocalDateStr(new Date())} required />
              {!filters.endDate && <p className="mt-1 text-xs text-red-500">End date is required</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium ${textS} mb-1`}>
                <FaSearch className="inline mr-1" /> DO Number
              </label>
              <div className="flex">
                <input type="text" className={`flex-1 p-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls}`} placeholder="Search DO #..." value={doSearch} onChange={(e) => setDoSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleDoSearch()} />
                <button onClick={handleDoSearch} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-r transition-colors" title="Search by DO number">
                  <FaSearch />
                </button>
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium ${textS} mb-1`}><FaTags className="inline mr-1" /> Status</label>
              <Select placeholder="Select Status..." value={statusOptions.find(option => option.value === filters.status)} options={statusOptions} onChange={(selected) => handleFilterChange('status', selected.value)} styles={selectStyles} />
            </div>
            <div>
              <label className={`block text-sm font-medium ${textS} mb-1`}><FaUser className="inline mr-1" /> Employee</label>
              <Select placeholder="Select Employee..." value={filters.employeeId} options={employeeOptions} onChange={(selected) => handleFilterChange('employeeId', selected)} styles={selectStyles} isClearable />
            </div>
          </div>
          
          <div className="mt-4 flex gap-2 justify-between items-center flex-wrap">
            <div className={`text-sm ${textM} italic`}>
              {filters.startDate && filters.endDate ? `Showing data from ${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}` : 'Select date range to view report data'}
              {filters.status && ` • Status: ${filters.status}`}
              {filters.employeeId && ` • Employee: ${filters.employeeId.label}`}
              {doSearch.trim() && ` • DO#: ${doSearch.trim()}`}
            </div>
            <button className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50" onClick={() => { if (!filters.startDate || !filters.endDate) { Swal.fire({ icon: 'error', title: 'Missing Required Fields', text: 'Please select both start and end dates' }); return; } fetchDeliveryOrdersData(); }} disabled={loading || !filters.startDate || !filters.endDate}>
              {loading ? <><FaSync className="animate-spin mr-2" /> Loading...</> : <><FaSearch className="mr-2" /> Search</>}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {reportData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-blue-500`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textS} font-medium`}>Total Orders</p>
                  <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{reportData.summary.totalCount}</h3>
                </div>
                <div className={`${darkMode ? 'bg-blue-900/40' : 'bg-blue-100'} p-3 rounded-full`}>
                  <FaFileInvoice className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} text-xl`} />
                </div>
              </div>
            </div>
            <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-green-500`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textS} font-medium`}>Total Amount</p>
                  <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{formatCurrency(reportData.summary.totalAmount)}</h3>
                </div>
                <div className={`${darkMode ? 'bg-green-900/40' : 'bg-green-100'} p-3 rounded-full`}>
                  <FaTags className={`${darkMode ? 'text-green-400' : 'text-green-600'} text-xl`} />
                </div>
              </div>
            </div>
            <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-purple-500`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textS} font-medium`}>Average Order Value</p>
                  <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{formatCurrency(reportData.summary.avgAmount)}</h3>
                </div>
                <div className={`${darkMode ? 'bg-purple-900/40' : 'bg-purple-100'} p-3 rounded-full`}>
                  <FaBuilding className={`${darkMode ? 'text-purple-400' : 'text-purple-600'} text-xl`} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Buttons */}
        <div className="flex gap-2 mb-6">
          <button className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50" onClick={exportToExcel} disabled={loading || !reportData}>
            <BsFileEarmarkExcel className="mr-2" /> Export to Excel
          </button>
          <button className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors disabled:opacity-50" onClick={exportToCSV} disabled={loading || !reportData}>
            <BsFiletypeCsv className="mr-2" /> Export to CSV
          </button>
        </div>

        {/* Bulk Action Bar */}
        {!readOnly && selectedOrders.length > 0 && (
          <div className={`${darkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-300'} border rounded-lg p-3 mb-4 flex items-center justify-between`}>
            <span className={`text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
              {selectedOrders.length} pending order{selectedOrders.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button onClick={() => setSelectedOrders([])} className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'}`}>
                Clear Selection
              </button>
              <button onClick={openBulkCompleteModal} className="px-3 py-1.5 rounded text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center">
                <FaCheckCircle className="mr-1.5" /> Complete Selected
              </button>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className={`${cardCls} rounded-lg shadow-md border overflow-hidden`}>
          <div className={`p-4 border-b ${borderCls} ${theadCls} flex items-center justify-between gap-3 flex-wrap`}>
            <h2 className={`font-semibold ${textP}`}>Delivery Orders List</h2>
            {!readOnly && reportData?.data?.length > 0 && (
              <div className="flex items-center gap-2">
                <span className={`text-xs ${textM}`}>Bulk select pending:</span>
                {[50, 100].map(n => (
                  <button
                    key={n}
                    onClick={() => selectPendingUpTo(n)}
                    disabled={bulkSelectLoading}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors disabled:opacity-50 ${darkMode ? 'bg-green-900/40 text-green-300 border-green-700 hover:bg-green-800/60' : 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200'}`}
                    title={`Select up to ${n} pending orders for bulk completion`}
                  >
                    {bulkSelectLoading ? <FaSync className="animate-spin inline" /> : `Up to ${n}`}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              <p className={`mt-4 ${textS}`}>Loading delivery orders data...</p>
            </div>
          ) : reportData && reportData.data.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className={`md:hidden divide-y ${borderCls}`}>
                {reportData.data.map((order) => (
                  <div key={order._id} className={`p-4 ${hoverRow} transition-colors`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{order.doNumber || order._id.substring(0, 8)}</div>
                        <div className={`text-xs mt-1 ${textM}`}>{formatDate(order.createdAt)}</div>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between text-sm">
                        <span className={textS}>Recipient:</span>
                        <span className={`font-medium ${textP} truncate ml-2`}>{order.recipientName || '—'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className={textS}>Created By:</span>
                        <span className={`font-medium ${textP}`}>{order.createdByName || order.createdBy?.name || '—'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className={textS}>Amount:</span>
                        <span className={`font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatCurrency(order.totalAmountWithGST || 0)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {!readOnly && (
                        <button onClick={() => openDateEditModal(order)} className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center transition-colors ${darkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                          <FaEdit className="mr-1" /> Edit Date
                        </button>
                      )}
                      <div className="relative group">
                        <button title="Print" className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${darkMode ? 'bg-emerald-700 hover:bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>
                          <FaPrint />
                        </button>
                        <div className={`absolute right-0 bottom-full mb-1 w-44 rounded-md shadow-lg z-50 hidden group-hover:block ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'}`}>
                          <button onClick={() => window.open('/delivery-orders/' + order._id, '_blank')} className={`block w-full text-left px-4 py-2 text-sm rounded-t-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print</button>
                          <button onClick={() => window.open('/delivery-orders-details/' + order._id, '_blank')} className={`block w-full text-left px-4 py-2 text-sm rounded-b-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print with Discount</button>
                        </div>
                      </div>
                      {!readOnly && (order.status === 'Completed' || order.status === 'Cancelled') && (
                        <button onClick={() => openResetModal(order)} className={`px-3 py-2 rounded-md text-xs font-medium flex items-center transition-colors ${darkMode ? 'bg-indigo-700 hover:bg-indigo-600 text-indigo-100' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}>
                          <FaRedo className="mr-1" /> Reset{order.resetHistory?.length ? ` ×${order.resetHistory.length + 1}` : ''}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className={`min-w-full divide-y ${borderCls}`}>
                  <thead className={theadCls}>
                    <tr>
                      <th className={`px-3 py-3 text-center`}>
                        {!readOnly && pendingOrders.length > 0 && (
                          <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAllPending} className="w-4 h-4 rounded cursor-pointer accent-green-600" title="Select all pending orders" />
                        )}
                      </th>
                      {['DO #', 'Date', 'Recipient', 'Created By', 'Salesperson', 'Amount', 'Status', 'Actions'].map(h => (
                        <th key={h} className={`px-4 py-3 text-left text-xs font-medium ${textS} uppercase tracking-wider`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${borderCls}`}>
                    {reportData.data.map((order) => (
                      <tr key={order._id} className={hoverRow}>
                        <td className="px-3 py-3 text-center">
                          {!readOnly && order.status === 'Pending' ? (
                            <input type="checkbox" checked={selectedOrders.includes(order._id)} onChange={() => toggleOrderSelection(order._id)} className="w-4 h-4 rounded cursor-pointer accent-green-600" />
                          ) : null}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-gray-900'}`}>{order.doNumber || order._id.substring(0, 8)}</td>
                        <td className={`px-4 py-3 text-sm ${textS}`}>{formatDate(order.createdAt)}</td>
                        <td className={`px-4 py-3 text-sm ${textP}`}>{order.recipientName}</td>
                        <td className={`px-4 py-3 text-sm ${textS}`}>{order.createdByName || order.createdBy?.name || '—'}</td>
                        <td className={`px-4 py-3 text-sm ${textS}`}>{order.salesmanNames || (order.salesman?.length > 0 ? order.salesman.map(s => s.name).join(', ') : '—')}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-gray-900'}`}>{formatCurrency(order.totalAmountWithGST || 0)}</td>
                        <td className="px-4 py-3 text-sm">{getStatusBadge(order.status)}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex space-x-2 items-center">
                            {!readOnly && (
                              <button onClick={() => openDateEditModal(order)} className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium transition-all shadow-sm border ${darkMode ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-800/60 border-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200'}`} title={`Edit Date - ${order.doNumber || order._id.substring(0, 8)}`}>
                                <FaEdit className="mr-1.5" /> Edit Date
                              </button>
                            )}
                            <div className="relative group">
                              <button title="Print" className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium transition-all shadow-sm border ${darkMode ? 'bg-emerald-900/40 text-emerald-300 hover:bg-emerald-800/60 border-emerald-700' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200'}`}>
                                <FaPrint className="mr-1.5" /> Print
                              </button>
                              <div className={`absolute right-0 mt-1 w-44 rounded-md shadow-lg z-50 hidden group-hover:block ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'}`}>
                                <button onClick={() => window.open('/delivery-orders/' + order._id, '_blank')} className={`block w-full text-left px-4 py-2 text-sm rounded-t-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print</button>
                                <button onClick={() => window.open('/delivery-orders-details/' + order._id, '_blank')} className={`block w-full text-left px-4 py-2 text-sm rounded-b-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print with Discount</button>
                              </div>
                            </div>
                            {!readOnly && (order.status === 'Completed' || order.status === 'Cancelled') && (
                              <button onClick={() => openResetModal(order)} className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium transition-all shadow-sm border ${darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-800/60 border-indigo-700' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border-indigo-200'}`} title={`Reset #${order.doNumber}`}>
                                <FaRedo className="mr-1.5" /> Reset{order.resetHistory?.length ? ` ×${order.resetHistory.length + 1}` : ''}
                              </button>
                            )}
                            {order.reset?.isReset && (
                              <span className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium border cursor-help ${darkMode ? 'bg-amber-900/30 text-amber-300 border-amber-700' : 'bg-amber-50 text-amber-700 border-amber-200'}`} title={`Reset ${(order.resetHistory?.length || 0) + 1} time(s) — last on ${formatDate(order.reset.resetDate)}\nReason: ${order.reset.resetReason || 'No reason provided'}`}>
                                <FaExclamationTriangle className="mr-1.5" /> Reset ×{(order.resetHistory?.length || 0) + 1}
                              </span>
                            )}
                            {order.status !== 'Completed' && order.status !== 'Cancelled' && !order.reset?.isReset && (
                              <span className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium border cursor-not-allowed ${darkMode ? 'bg-gray-700 text-gray-500 border-gray-600' : 'bg-gray-100 text-gray-500 border-gray-200'}`} title="Only completed or cancelled orders can be reset">
                                <FaRedo className="mr-1.5 opacity-50" /> N/A
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {reportData.totalPages > 1 && (
                <div className={`flex items-center justify-between px-4 py-3 border-t ${borderCls} sm:px-6`}>
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className={`px-4 py-2 border text-sm font-medium rounded-md ${page === 1 ? (darkMode ? 'bg-gray-700 text-gray-500 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-300') : (darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300')}`}>Previous</button>
                    <button onClick={() => handlePageChange(page + 1)} disabled={page === reportData.totalPages} className={`px-4 py-2 border text-sm font-medium rounded-md ${page === reportData.totalPages ? (darkMode ? 'bg-gray-700 text-gray-500 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-300') : (darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300')}`}>Next</button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <p className={`text-sm ${textS}`}>
                      Showing <span className="font-medium">{((page - 1) * limit) + 1}</span> to <span className="font-medium">{Math.min(page * limit, reportData.totalRecords)}</span> of <span className="font-medium">{reportData.totalRecords}</span> results
                    </p>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className={`px-2 py-2 rounded-l-md border text-sm font-medium ${page === 1 ? (darkMode ? 'text-gray-600 bg-gray-800 border-gray-700' : 'text-gray-300 bg-white border-gray-300') : (darkMode ? 'text-gray-300 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50')}`}>
                        <FaArrowLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: Math.min(5, reportData.totalPages) }, (_, i) => {
                        let p;
                        if (reportData.totalPages <= 5) p = i + 1;
                        else if (page <= 3) { p = i + 1; if (i === 4) p = reportData.totalPages; }
                        else if (page >= reportData.totalPages - 2) p = reportData.totalPages - 4 + i;
                        else p = page - 2 + i;
                        return (
                          <button key={p} onClick={() => handlePageChange(p)} className={`px-4 py-2 border text-sm font-medium ${page === p ? (darkMode ? 'z-10 bg-blue-900/50 border-blue-500 text-blue-400' : 'z-10 bg-blue-50 border-blue-500 text-blue-600') : (darkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50')}`}>{p}</button>
                        );
                      })}
                      <button onClick={() => handlePageChange(page + 1)} disabled={page === reportData.totalPages} className={`px-2 py-2 rounded-r-md border text-sm font-medium ${page === reportData.totalPages ? (darkMode ? 'text-gray-600 bg-gray-800 border-gray-700' : 'text-gray-300 bg-white border-gray-300') : (darkMode ? 'text-gray-300 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50')}`}>
                        <FaArrowRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center">
              <p className={textS}>No delivery orders found with the selected filters.</p>
              <button onClick={resetFilters} className="mt-4 inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                <FaSync className="mr-2" /> Reset Filters
              </button>
            </div>
          )}
        </div>

        {/* Reset Order Modal */}
        {isResetModalOpen && selectedOrderToReset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-lg`}>
              <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'bg-indigo-900/30 border-gray-700' : 'bg-indigo-50 border-gray-200'}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-indigo-300' : 'text-indigo-800'} flex items-center`}>
                  <FaRedo className={`mr-2 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} /> Reset Delivery Order{selectedOrderToReset?.reset?.isReset ? ` (Reset #${(selectedOrderToReset.resetHistory?.length || 0) + 1})` : ''}
                </h3>
                <button onClick={closeResetModal} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-6">
                <div className={`${darkMode ? 'bg-amber-900/20 border-amber-600' : 'bg-amber-50 border-amber-400'} border-l-4 p-4 mb-6 rounded-r`}>
                  <div className="flex">
                    <FaExclamationTriangle className={`h-5 w-5 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Caution</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-amber-400/80' : 'text-amber-600'}`}>{selectedOrderToReset?.reset?.isReset ? `This order has already been reset ${(selectedOrderToReset.resetHistory?.length || 0) + 1} time(s). Resetting again will set it back to Pending.` : 'This action will mark the order as reset and set it back to Pending.'}</p>
                    </div>
                  </div>
                </div>
                <div className={`${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} border rounded-md p-4 mb-6 shadow-sm`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>DO Number</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{selectedOrderToReset?.doNumber || selectedOrderToReset?._id?.substring(0, 8)}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Date Created</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{formatDate(selectedOrderToReset?.createdAt)}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Amount</p><p className={`text-sm font-semibold mt-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{formatCurrency(selectedOrderToReset?.totalAmountWithGST || 0)}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Recipient</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{selectedOrderToReset?.recipientName}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Created By</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{selectedOrderToReset?.createdByName || 'Unknown'}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Status</p><div className="mt-1">{getStatusBadge(selectedOrderToReset.status)}</div></div>
                  </div>
                </div>
                {selectedOrderToReset?.resetHistory?.length > 0 && (
                  <div className={`${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-md p-4 mb-6`}>
                    <p className={`text-xs uppercase font-medium ${textM} mb-2`}>Previous Resets ({selectedOrderToReset.resetHistory.length})</p>
                    <ul className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedOrderToReset.resetHistory.slice().reverse().map((r, i) => (
                        <li key={i} className={`text-sm flex justify-between gap-3 ${textS}`}>
                          <span className="truncate" title={r.resetReason || 'No reason'}>{r.resetReason || 'No reason provided'}</span>
                          <span className={`whitespace-nowrap ${textM}`}>{formatDate(r.resetDate)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); if (resetReason.trim()) resetDeliveryOrder(); }}>
                  <div className="mb-4">
                    <label className={`block text-sm font-medium ${textS} mb-1`}>Reason for Reset <span className="text-red-500">*</span></label>
                    <textarea className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!resetReason.trim() ? (darkMode ? 'border-red-600 bg-red-900/20' : 'border-red-300 bg-red-50') : inputCls}`} rows="4" value={resetReason} onChange={(e) => setResetReason(e.target.value)} placeholder="Please provide a detailed reason..." required />
                    {!resetReason.trim() && <p className="mt-1 text-sm text-red-500">Reason is required for audit purposes</p>}
                    <p className={`mt-2 text-xs ${textM}`}>This information will be stored with the order history.</p>
                  </div>
                  <div className={`flex justify-end pt-4 border-t ${borderCls}`}>
                    <button type="button" onClick={closeResetModal} className={`px-4 py-2 border rounded-md shadow-sm mr-3 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                    <button type="submit" className={`px-4 py-2 rounded-md shadow-sm flex items-center ${!resetReason.trim() || resetLoading ? (darkMode ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gray-300 cursor-not-allowed text-gray-500') : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`} disabled={resetLoading || !resetReason.trim()}>
                      {resetLoading ? <><FaSync className="animate-spin mr-2" /> Resetting...</> : <><FaExclamationTriangle className="mr-2" /> Confirm Reset</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Date Modal */}
        {isDateEditModalOpen && selectedOrderForDateEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-md`}>
              <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'bg-blue-900/30 border-gray-700' : 'bg-blue-50 border-gray-200'}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-800'} flex items-center`}>
                  <FaCalendarAlt className={`mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} /> Edit Created Date
                </h3>
                <button onClick={closeDateEditModal} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-6">
                <div className={`${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} border rounded-md p-4 mb-6 shadow-sm`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>DO Number</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{selectedOrderForDateEdit?.doNumber || selectedOrderForDateEdit?._id?.substring(0, 8)}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Current Date</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{formatDate(selectedOrderForDateEdit?.createdAt)}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Recipient</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{selectedOrderForDateEdit?.recipientName}</p></div>
                    <div><p className={`text-xs uppercase font-medium ${textM}`}>Status</p><div className="mt-1">{getStatusBadge(selectedOrderForDateEdit.status)}</div></div>
                  </div>
                </div>
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${textS} mb-2`}>New Date <span className="text-red-500">*</span></label>
                  <DatePicker
                    selected={newCreatedDate}
                    onChange={(date) => setNewCreatedDate(date)}
                    dateFormat="dd/MM/yyyy"
                    className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls}`}
                    placeholderText="Select new date"
                    minDate={selectedOrderForDateEdit ? getFinancialYearBounds(selectedOrderForDateEdit.createdAt).fyStart : undefined}
                    maxDate={(() => { if (!selectedOrderForDateEdit) return new Date(); const { fyEnd } = getFinancialYearBounds(selectedOrderForDateEdit.createdAt); return fyEnd < new Date() ? fyEnd : new Date(); })()}
                    showMonthDropdown showYearDropdown dropdownMode="select"
                  />
                  <p className={`text-xs ${textM} mt-1`}>
                    {selectedOrderForDateEdit && (() => { const { fyStart, fyEnd } = getFinancialYearBounds(selectedOrderForDateEdit.createdAt); return `Allowed: ${fyStart.toLocaleDateString('en-GB')} – ${fyEnd.toLocaleDateString('en-GB')} (Financial Year)`; })()}
                  </p>
                </div>
                <div className={`flex justify-end pt-4 border-t ${borderCls}`}>
                  <button type="button" onClick={closeDateEditModal} className={`px-4 py-2 border rounded-md shadow-sm mr-3 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                  <button type="button" onClick={updateDeliveryOrderDate} className={`px-4 py-2 rounded-md shadow-sm flex items-center ${dateEditLoading || !newCreatedDate ? (darkMode ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gray-300 cursor-not-allowed text-gray-500') : 'bg-blue-600 hover:bg-blue-700 text-white'}`} disabled={dateEditLoading || !newCreatedDate}>
                    {dateEditLoading ? <><FaSync className="animate-spin mr-2" /> Saving...</> : <><FaCalendarAlt className="mr-2" /> Update Date</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Bulk Complete Modal */}
        {showBulkCompleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col`}>
              <div className={`p-4 border-b flex justify-between items-center flex-shrink-0 ${darkMode ? 'bg-green-900/30 border-gray-700' : 'bg-green-50 border-gray-200'}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-green-300' : 'text-green-800'} flex items-center`}>
                  <FaCheckCircle className={`mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} /> Complete {selectedOrders.length} Order{selectedOrders.length > 1 ? 's' : ''}
                </h3>
                <button onClick={closeBulkCompleteModal} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <p className={`text-sm mb-4 ${textS}`}>{bulkUseSingleInvoice ? 'Enter one invoice number for all selected delivery orders:' : 'Enter invoice numbers for each selected delivery order:'}</p>
                <label className={`flex items-center gap-2 mb-4 text-sm font-medium ${textP}`}>
                  <input
                    type="checkbox"
                    checked={bulkUseSingleInvoice}
                    onChange={(e) => setBulkUseSingleInvoice(e.target.checked)}
                    className="w-4 h-4 rounded cursor-pointer accent-green-600"
                  />
                  Use single invoice number for all selected DOs
                </label>
                {bulkUseSingleInvoice && (
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${textS}`}>Invoice Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Invoice Number *"
                      value={bulkSingleInvoiceNumber}
                      onChange={(e) => setBulkSingleInvoiceNumber(e.target.value)}
                      className={`w-full p-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${!bulkSingleInvoiceNumber.trim() ? (darkMode ? 'border-red-600 bg-red-900/20 text-gray-200 placeholder-gray-400' : 'border-red-300 bg-red-50') : inputCls}`}
                    />
                  </div>
                )}
                <div className="space-y-3">
                  {selectedOrders.map((orderId, idx) => {
                    const order = bulkOrderCache[orderId] || reportData?.data?.find(o => o._id === orderId);
                    if (!order) return null;
                    return (
                      <div key={orderId} className={`${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-md p-3`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{order.doNumber}</span>
                              <span className={`text-xs ${textM}`}>{formatDate(order.createdAt)}</span>
                            </div>
                            <div className={`text-xs mt-0.5 ${textS} truncate`}>{order.recipientName} &bull; {formatCurrency(order.totalAmountWithGST || 0)}</div>
                          </div>
                          {!bulkUseSingleInvoice && <div className="w-48">
                            <input
                              type="text"
                              placeholder="Invoice Number *"
                              value={bulkInvoiceNumbers[orderId] || ''}
                              onChange={(e) => setBulkInvoiceNumbers(prev => ({ ...prev, [orderId]: e.target.value }))}
                              className={`w-full p-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${!bulkInvoiceNumbers[orderId]?.trim() ? (darkMode ? 'border-red-600 bg-red-900/20' : 'border-red-300 bg-red-50') : inputCls}`}
                            />
                          </div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={`flex justify-end p-4 border-t flex-shrink-0 ${borderCls}`}>
                <button type="button" onClick={closeBulkCompleteModal} className={`px-4 py-2 border rounded-md shadow-sm mr-3 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                <button type="button" onClick={handleBulkComplete} className={`px-4 py-2 rounded-md shadow-sm flex items-center ${bulkCompleteLoading ? (darkMode ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gray-300 cursor-not-allowed text-gray-500') : 'bg-green-600 hover:bg-green-700 text-white'}`} disabled={bulkCompleteLoading}>
                  {bulkCompleteLoading ? <><FaSync className="animate-spin mr-2" /> Completing...</> : <><FaCheckCircle className="mr-2" /> Complete All</>}
                </button>
              </div>
            </div>
          </div>
        )}
        </>)}

        {/* Tiles Delivery Orders Tab */}
        {activeReportTab === 'tiles' && (<>
          {/* Tiles Filters */}
          <div className={`${cardCls} rounded-lg shadow-md border p-4 mb-6`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-lg font-medium flex items-center ${textP}`}>
                <FaFilter className={`mr-2 ${textS}`} /> Report Filters
              </h2>
              <button className={`flex items-center px-4 py-2 rounded text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`} onClick={resetTilesFilters}>
                <FaEraser className="mr-2" /> Reset
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className={`block text-sm font-medium ${textS} mb-1`}>
                  <FaCalendarAlt className="inline mr-1" /> Start Date <span className="text-red-500">*</span>
                </label>
                <input type="date" className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls}`} value={tilesFilters.startDate} onChange={(e) => handleTilesFilterChange('startDate', e.target.value)} max={toLocalDateStr(new Date())} />
              </div>
              <div>
                <label className={`block text-sm font-medium ${textS} mb-1`}>
                  <FaCalendarAlt className="inline mr-1" /> End Date <span className="text-red-500">*</span>
                </label>
                <input type="date" className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls}`} value={tilesFilters.endDate} onChange={(e) => handleTilesFilterChange('endDate', e.target.value)} min={tilesFilters.startDate} max={toLocalDateStr(new Date())} />
              </div>
              <div>
                <label className={`block text-sm font-medium ${textS} mb-1`}>
                  <FaSearch className="inline mr-1" /> DO Number
                </label>
                <div className="flex">
                  <input type="text" className={`flex-1 p-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls}`} placeholder="Search DO #..." value={tilesDoSearch} onChange={(e) => setTilesDoSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleTilesDoSearch()} />
                  <button onClick={handleTilesDoSearch} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-r transition-colors" title="Search by DO number">
                    <FaSearch />
                  </button>
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium ${textS} mb-1`}><FaTags className="inline mr-1" /> Status</label>
                <Select placeholder="Select Status..." value={statusOptions.find(option => option.value === tilesFilters.status)} options={statusOptions} onChange={(selected) => handleTilesFilterChange('status', selected.value)} styles={selectStyles} />
              </div>
              <div>
                <label className={`block text-sm font-medium ${textS} mb-1`}><FaUser className="inline mr-1" /> Employee</label>
                <Select placeholder="Select Employee..." value={tilesFilters.employeeId} options={employeeOptions} onChange={(selected) => handleTilesFilterChange('employeeId', selected)} styles={selectStyles} isClearable />
              </div>
            </div>
            <div className="mt-4 flex gap-2 justify-between items-center flex-wrap">
              <div className={`text-sm ${textM} italic`}>
                {tilesFilters.startDate && tilesFilters.endDate ? `Showing data from ${formatDate(tilesFilters.startDate)} to ${formatDate(tilesFilters.endDate)}` : 'Select date range to view report data'}
                {tilesFilters.status && ` • Status: ${tilesFilters.status}`}
                {tilesFilters.employeeId && ` • Employee: ${tilesFilters.employeeId.label}`}
                {tilesDoSearch.trim() && ` • DO#: ${tilesDoSearch.trim()}`}
              </div>
              <button className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50" onClick={fetchTilesDeliveryOrders} disabled={tilesLoading || !tilesFilters.startDate || !tilesFilters.endDate}>
                {tilesLoading ? <><FaSync className="animate-spin mr-2" /> Loading...</> : <><FaSearch className="mr-2" /> Search</>}
              </button>
            </div>
          </div>

          {/* Tiles Summary Cards */}
          {tilesReportData?.stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-blue-500`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${textS} font-medium`}>Total Orders</p>
                    <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{tilesReportData.stats.totalOrders || tilesReportData.pagination?.total || 0}</h3>
                  </div>
                  <div className={`${darkMode ? 'bg-blue-900/40' : 'bg-blue-100'} p-3 rounded-full`}>
                    <FaFileInvoice className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} text-xl`} />
                  </div>
                </div>
              </div>
              <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-green-500`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${textS} font-medium`}>Total Amount</p>
                    <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{formatCurrency(tilesReportData.stats.totalAmount || 0)}</h3>
                  </div>
                  <div className={`${darkMode ? 'bg-green-900/40' : 'bg-green-100'} p-3 rounded-full`}>
                    <FaTags className={`${darkMode ? 'text-green-400' : 'text-green-600'} text-xl`} />
                  </div>
                </div>
              </div>
              <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-yellow-500`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${textS} font-medium`}>Pending</p>
                    <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{tilesReportData.stats.pendingOrders || 0}</h3>
                  </div>
                  <div className={`${darkMode ? 'bg-yellow-900/40' : 'bg-yellow-100'} p-3 rounded-full`}>
                    <FaExclamationTriangle className={`${darkMode ? 'text-yellow-400' : 'text-yellow-600'} text-xl`} />
                  </div>
                </div>
              </div>
              <div className={`${cardCls} rounded-lg shadow-md border p-5 border-l-4 border-l-purple-500`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${textS} font-medium`}>Completed</p>
                    <h3 className={`text-2xl font-bold mt-1 ${textP}`}>{tilesReportData.stats.completedOrders || 0}</h3>
                  </div>
                  <div className={`${darkMode ? 'bg-purple-900/40' : 'bg-purple-100'} p-3 rounded-full`}>
                    <FaCheckCircle className={`${darkMode ? 'text-purple-400' : 'text-purple-600'} text-xl`} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tiles Data Table */}
          <div className={`${cardCls} rounded-lg shadow-md border overflow-hidden`}>
            <div className={`p-4 border-b ${borderCls} ${theadCls}`}>
              <h2 className={`font-semibold ${textP}`}>Tiles Delivery Orders List</h2>
            </div>
            
            {tilesLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <p className={`mt-4 ${textS}`}>Loading tiles delivery orders data...</p>
              </div>
            ) : tilesReportData && tilesReportData.orders && tilesReportData.orders.length > 0 ? (
              <>
                {/* Mobile Card View */}
                <div className={`md:hidden divide-y ${borderCls}`}>
                  {tilesReportData.orders.map((order) => (
                    <div key={order._id} className={`p-4 ${hoverRow} transition-colors`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{order.doNumber || '—'}</div>
                          <div className={`text-xs mt-1 ${textM}`}>{formatDate(order.createdAt)}</div>
                        </div>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span className={textS}>Recipient:</span>
                          <span className={`font-medium ${textP} truncate ml-2`}>{getTilesRecipientName(order)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={textS}>Type:</span>
                          <span className={`font-medium ${textP}`}>{order.recipientType || '—'}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={textS}>Location:</span>
                          <span className={`font-medium ${textP}`}>{getTilesLocationName(order)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={textS}>Items:</span>
                          <span className={`font-medium ${textP}`}>{order.itemsCount || ((order.items?.length || 0) + (order.stockItems?.length || 0))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={textS}>Amount:</span>
                          <span className={`font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatCurrency(order.totalAmountWithGST || 0)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => openTilesDateEditModal(order)} className={`flex-1 px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center transition-colors ${darkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                          <FaEdit className="mr-1" /> Edit Date
                        </button>
                        <div className="relative group">
                          <button title="Print" className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${darkMode ? 'bg-emerald-700 hover:bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>
                            <FaPrint />
                          </button>
                          <div className={`absolute right-0 bottom-full mb-1 w-44 rounded-md shadow-lg z-50 hidden group-hover:block ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'}`}>
                            <button onClick={() => handleTilesPrint(order._id, 'basic')} className={`block w-full text-left px-4 py-2 text-sm rounded-t-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print</button>
                            <button onClick={() => handleTilesPrint(order._id, 'discount')} className={`block w-full text-left px-4 py-2 text-sm rounded-b-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print with Discount</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className={`min-w-full divide-y ${borderCls}`}>
                    <thead className={theadCls}>
                      <tr>
                        {['DO #', 'Date', 'Type', 'Recipient', 'Location', 'Created By', 'Salesperson', 'Items', 'Amount', 'Status', 'Actions'].map(h => (
                          <th key={h} className={`px-4 py-3 text-left text-xs font-medium ${textS} uppercase tracking-wider`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${borderCls}`}>
                      {tilesReportData.orders.map((order) => (
                        <tr key={order._id} className={hoverRow}>
                          <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-gray-900'}`}>{order.doNumber || '—'}</td>
                          <td className={`px-4 py-3 text-sm ${textS}`}>{formatDate(order.createdAt)}</td>
                          <td className={`px-4 py-3 text-sm`}>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${order.recipientType === 'Customer' ? (darkMode ? 'bg-cyan-900/40 text-cyan-300' : 'bg-cyan-100 text-cyan-800') : (darkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-800')}`}>
                              {order.recipientType || '—'}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm ${textP}`}>{getTilesRecipientName(order)}</td>
                          <td className={`px-4 py-3 text-sm ${textS}`}>{getTilesLocationName(order)}</td>
                          <td className={`px-4 py-3 text-sm ${textS}`}>{getTilesCreatedByName(order)}</td>
                          <td className={`px-4 py-3 text-sm ${textS}`}>{getTilesSalesmanNames(order)}</td>
                          <td className={`px-4 py-3 text-sm text-center ${textP}`}>{order.itemsCount || ((order.items?.length || 0) + (order.stockItems?.length || 0))}</td>
                          <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-gray-900'}`}>{formatCurrency(order.totalAmountWithGST || 0)}</td>
                          <td className="px-4 py-3 text-sm">{getStatusBadge(order.status)}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2 items-center">
                              <button onClick={() => openTilesDateEditModal(order)} className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium transition-all shadow-sm border ${darkMode ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-800/60 border-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200'}`} title={`Edit Date - ${order.doNumber}`}>
                                <FaEdit className="mr-1.5" /> Edit Date
                              </button>
                              <div className="relative group">
                                <button title="Print" className={`px-2.5 py-1.5 rounded-md flex items-center text-xs font-medium transition-all shadow-sm border ${darkMode ? 'bg-emerald-900/40 text-emerald-300 hover:bg-emerald-800/60 border-emerald-700' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200'}`}>
                                  <FaPrint className="mr-1.5" /> Print
                                </button>
                                <div className={`absolute right-0 mt-1 w-44 rounded-md shadow-lg z-50 hidden group-hover:block ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'}`}>
                                  <button onClick={() => handleTilesPrint(order._id, 'basic')} className={`block w-full text-left px-4 py-2 text-sm rounded-t-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print</button>
                                  <button onClick={() => handleTilesPrint(order._id, 'discount')} className={`block w-full text-left px-4 py-2 text-sm rounded-b-md ${darkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'}`}>Print with Discount</button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Tiles Pagination */}
                {tilesReportData.pagination && tilesReportData.pagination.pages > 1 && (
                  <div className={`flex items-center justify-between px-4 py-3 border-t ${borderCls} sm:px-6`}>
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button onClick={() => setTilesPage(Math.max(1, tilesPage - 1))} disabled={tilesPage === 1} className={`px-4 py-2 border text-sm font-medium rounded-md ${tilesPage === 1 ? (darkMode ? 'bg-gray-700 text-gray-500 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-300') : (darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300')}`}>Previous</button>
                      <button onClick={() => setTilesPage(tilesPage + 1)} disabled={tilesPage === tilesReportData.pagination.pages} className={`px-4 py-2 border text-sm font-medium rounded-md ${tilesPage === tilesReportData.pagination.pages ? (darkMode ? 'bg-gray-700 text-gray-500 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-300') : (darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300')}`}>Next</button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <p className={`text-sm ${textS}`}>
                        Showing <span className="font-medium">{((tilesPage - 1) * 10) + 1}</span> to <span className="font-medium">{Math.min(tilesPage * 10, tilesReportData.pagination.total)}</span> of <span className="font-medium">{tilesReportData.pagination.total}</span> results
                      </p>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button onClick={() => setTilesPage(Math.max(1, tilesPage - 1))} disabled={tilesPage === 1} className={`px-2 py-2 rounded-l-md border text-sm font-medium ${tilesPage === 1 ? (darkMode ? 'text-gray-600 bg-gray-800 border-gray-700' : 'text-gray-300 bg-white border-gray-300') : (darkMode ? 'text-gray-300 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50')}`}>
                          <FaArrowLeft className="h-5 w-5" />
                        </button>
                        {Array.from({ length: Math.min(5, tilesReportData.pagination.pages) }, (_, i) => {
                          let p;
                          const totalPages = tilesReportData.pagination.pages;
                          if (totalPages <= 5) p = i + 1;
                          else if (tilesPage <= 3) { p = i + 1; if (i === 4) p = totalPages; }
                          else if (tilesPage >= totalPages - 2) p = totalPages - 4 + i;
                          else p = tilesPage - 2 + i;
                          return (
                            <button key={p} onClick={() => setTilesPage(p)} className={`px-4 py-2 border text-sm font-medium ${tilesPage === p ? (darkMode ? 'z-10 bg-blue-900/50 border-blue-500 text-blue-400' : 'z-10 bg-blue-50 border-blue-500 text-blue-600') : (darkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50')}`}>{p}</button>
                          );
                        })}
                        <button onClick={() => setTilesPage(tilesPage + 1)} disabled={tilesPage === tilesReportData.pagination.pages} className={`px-2 py-2 rounded-r-md border text-sm font-medium ${tilesPage === tilesReportData.pagination.pages ? (darkMode ? 'text-gray-600 bg-gray-800 border-gray-700' : 'text-gray-300 bg-white border-gray-300') : (darkMode ? 'text-gray-300 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50')}`}>
                          <FaArrowRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center">
                <p className={textS}>No tiles delivery orders found with the selected filters.</p>
                <button onClick={resetTilesFilters} className="mt-4 inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                  <FaSync className="mr-2" /> Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Tiles Edit Date Modal */}
          {tilesDateEditModalOpen && tilesSelectedOrder && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-md`}>
                <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'bg-blue-900/30 border-gray-700' : 'bg-blue-50 border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-800'} flex items-center`}>
                    <FaCalendarAlt className={`mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} /> Edit Created Date
                  </h3>
                  <button onClick={closeTilesDateEditModal} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="p-6">
                  <div className={`${darkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} border rounded-md p-4 mb-6 shadow-sm`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div><p className={`text-xs uppercase font-medium ${textM}`}>DO Number</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{tilesSelectedOrder?.doNumber || '—'}</p></div>
                      <div><p className={`text-xs uppercase font-medium ${textM}`}>Current Date</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{formatDate(tilesSelectedOrder?.createdAt)}</p></div>
                      <div><p className={`text-xs uppercase font-medium ${textM}`}>Recipient</p><p className={`text-sm font-semibold mt-1 ${textP}`}>{tilesSelectedOrder?.recipientName || '—'}</p></div>
                      <div><p className={`text-xs uppercase font-medium ${textM}`}>Status</p><div className="mt-1">{getStatusBadge(tilesSelectedOrder.status)}</div></div>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className={`block text-sm font-medium ${textS} mb-2`}>New Date <span className="text-red-500">*</span></label>
                    <DatePicker
                      selected={tilesNewDate}
                      onChange={(date) => setTilesNewDate(date)}
                      dateFormat="dd/MM/yyyy"
                      className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputCls}`}
                      placeholderText="Select new date"
                      minDate={tilesSelectedOrder ? getFinancialYearBounds(tilesSelectedOrder.createdAt).fyStart : undefined}
                      maxDate={(() => { if (!tilesSelectedOrder) return new Date(); const { fyEnd } = getFinancialYearBounds(tilesSelectedOrder.createdAt); return fyEnd < new Date() ? fyEnd : new Date(); })()}
                      showMonthDropdown showYearDropdown dropdownMode="select"
                    />
                    <p className={`text-xs ${textM} mt-1`}>
                      {tilesSelectedOrder && (() => { const { fyStart, fyEnd } = getFinancialYearBounds(tilesSelectedOrder.createdAt); return `Allowed: ${fyStart.toLocaleDateString('en-GB')} – ${fyEnd.toLocaleDateString('en-GB')} (Financial Year)`; })()}
                    </p>
                  </div>
                  <div className={`flex justify-end pt-4 border-t ${borderCls}`}>
                    <button type="button" onClick={closeTilesDateEditModal} className={`px-4 py-2 border rounded-md shadow-sm mr-3 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                    <button type="button" onClick={updateTilesDeliveryOrderDate} className={`px-4 py-2 rounded-md shadow-sm flex items-center ${tilesDateEditLoading || !tilesNewDate ? (darkMode ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gray-300 cursor-not-allowed text-gray-500') : 'bg-blue-600 hover:bg-blue-700 text-white'}`} disabled={tilesDateEditLoading || !tilesNewDate}>
                      {tilesDateEditLoading ? <><FaSync className="animate-spin mr-2" /> Saving...</> : <><FaCalendarAlt className="mr-2" /> Update Date</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}
